import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import { FeatureAccessService } from './feature-access.service';

/**
 * Tenant-enablement guard for the added report destinations (ESG, CO2, the two
 * cockpits, Spend Intelligence).
 *
 * The routes live in `app.routes.ts`, which every tenant's build ships, so the
 * only thing separating a tenant that has these pages from one that does not is
 * a `tblPage` row. This guard is what enforces that: no row in the tenant's
 * sidebar payload, no page. It reads the route's own `path`, so adding a
 * destination needs no new guard — only that `tblPage.Link` equals the path.
 *
 * Deliberately NOT a permission check. A destination-level key evaluated with
 * `has$` default-denies until the RBAC slice hydrates and denies outright on a
 * tenant with no permission catalogue, which would lock a client out of a page
 * they just paid for. Access *within* a destination is already governed
 * per-page by `visibleUnlessGated$` in ReportViewComponent, which is permissive
 * on tenants without RBAC. If a whole destination ever needs role gating, add it
 * there with the same permissive-on-unknown-catalogue semantics — not here.
 *
 * `/dashboard` intentionally carries no guard at all: it is the `**` wildcard
 * target and the redirect destination below, so guarding it risks a loop.
 *
 * Same caveat as every other guard here — the sidebar payload is a localStorage
 * cache and is a UX gate, not a security boundary (#11774).
 */
export const reportDestinationGuard: CanActivateFn = (route) => {
  const featureAccess = inject(FeatureAccessService);
  const router = inject(Router);

  const link = route.routeConfig?.path;
  if (link && featureAccess.isPageActive(link)) {
    return true;
  }
  return router.parseUrl('dashboard');
};
